import {AnchorValues} from "../../../../../../tools/replace-anchors/replace-anchors";
import {numberToTwoDecimalsLocaleString} from "../../../../../../tools/formatting/number-to-two-decimals-locale-string";
import {numberToWholeNumberLocaleString} from "../../../../../../tools/formatting/number-to-whole-number-locale-string";
import {formatDuration} from "./format-duration";
import {ExternalFindings} from "../../../external-findings";
import {formatAfibBurden, formatBurden} from "./format-burden";


interface ExternalFindingFieldFormatter {
  format(value: number | undefined, locale: Intl.LocalesArgument): string | undefined;
}

interface ExternalFindingsFormats {
  [key: (keyof ExternalFindings)]: ExternalFindingFieldFormatter;
}

const decimalNumberFormatter: ExternalFindingFieldFormatter = {
  format: (value, locale) => value === undefined
    ? undefined
    : numberToTwoDecimalsLocaleString(value!, locale)
};

const wholeNumberFormatter: ExternalFindingFieldFormatter = {
  format: (value, locale) => value === undefined
    ? undefined
    : numberToWholeNumberLocaleString(value!, locale)
};

const durationFormatter: ExternalFindingFieldFormatter = {
  format: (value, locale) => value === undefined
    ? undefined
    : formatDuration(value!, locale)
};

const burdenFormatter: ExternalFindingFieldFormatter = {
  format: (value, locale) => value === undefined
    ? undefined
    : formatBurden(value!, locale)
};

const afibBurdenFormatter: ExternalFindingFieldFormatter = {
  format: (value, locale) => value === undefined
    ? undefined
    : formatAfibBurden(value!, locale)
};

export function createExternalFindingsFormats(): ExternalFindingsFormats {

  return {
    afibBurden: afibBurdenFormatter,
    afibTotalDuration: durationFormatter,
    averageHeartRate: wholeNumberFormatter,
    minimumHeartRate: wholeNumberFormatter,
    afibMinimumHeartRate: wholeNumberFormatter,
    sinusMinimumHeartRate: wholeNumberFormatter,
    maximumHeartRate: wholeNumberFormatter,
    afibMaximumHeartRate: wholeNumberFormatter,
    sinusMaximumHeartRate: wholeNumberFormatter,
    afibAverageHeartRate: wholeNumberFormatter,
    pausesOverThreshold: decimalNumberFormatter,
    ventricularBeats: decimalNumberFormatter,
    ventricularTachycardias: decimalNumberFormatter,
    ventricularTachycardiasMaxHeartRate: wholeNumberFormatter,
    ventricularTachycardiasLongestDurationMaxHeartRate: wholeNumberFormatter,
    ventricularTachycardiasLongestDurationBeats: decimalNumberFormatter,
    ventricularTachycardias_deprecated: decimalNumberFormatter,
    ventricularTachycardiasLongestBeats_deprecated: decimalNumberFormatter,
    ventricularTachycardiasLongestBeatsMaxHeartRate_deprecated: wholeNumberFormatter,
    ventricularTachycardiasMaxHeartRate_deprecated: wholeNumberFormatter,
    ventricularTachycardiasMaxHeartRateBeats_deprecated: wholeNumberFormatter,
    ventricularTachycardiasLongestDurationMaxHeartRate_deprecated: wholeNumberFormatter,
    ventricularTachycardiasLongestDurationBeats_deprecated: decimalNumberFormatter,
    slowVentricularRhythmCount: decimalNumberFormatter,
    slowVentricularRhythmLongestDuration: durationFormatter,
    slowVentricularRhythmLongestDurationBeats: decimalNumberFormatter,
    slowVentricularRhythmLongestDurationMaximumHeartRate: wholeNumberFormatter,
    ventricularCoupletsCount: decimalNumberFormatter,
    atrialBeats: decimalNumberFormatter,
    atrialTachycardias: decimalNumberFormatter,
    atrialTachycardiasLongestBeats: decimalNumberFormatter,
    atrialTachycardiasLongestBeatsMaximumHeartRate: wholeNumberFormatter,
    atrialTachycardiasMaximumHeartRate: wholeNumberFormatter,
    atrialTachycardiasMaximumHeartRateBeats: wholeNumberFormatter,
    atrialTachycardiasLongestDurationBeats: decimalNumberFormatter,

    pausesOverThresholdLongestDuration: durationFormatter,
    ventricularTachycardiasLongestDuration: durationFormatter,
    ventricularTachycardiasLongestDuration_deprecated: durationFormatter,
    ventricularTachycardiasMaxHeartRateDuration_deprecated: durationFormatter,
    atrialTachycardiasLongestDuration: durationFormatter,
    atrialTachycardiasMaximumHeartRateDuration: durationFormatter,
    atrialTachycardiasLongestDurationMaxHeartRate: wholeNumberFormatter,
    ventricularBeatsBurden: burdenFormatter,
    atrialBeatsBurden: burdenFormatter,
    vRuns: decimalNumberFormatter,
    sRuns: decimalNumberFormatter
  };
}


export function formatExternalFindings(
  externalFindings: ExternalFindings,
  locale: Intl.LocalesArgument,
): AnchorValues {
  return Object.fromEntries(
    Object.entries(createExternalFindingsFormats())
      .map(([formatField, formatter]) =>
        [
          formatField,
          formatter.format(externalFindings[formatField], locale)
        ]
      )
  );
}
